import React, { useState } from "react";
import { Checkbox, MenuItem, Select, Switch, TextField } from "@mui/material";
import { MdEditNote, MdKeyboardArrowDown } from "react-icons/md";
import { useTranslation } from "react-i18next";
import noImage from "../../../../../assets/images/noImage.png";
import { productNote } from "../../../../Shipper/Customer/MainPages/Orders/Components/DeliveryRoutes/lineNoteText";

// The saleable quantities a line can be given, most first.
export const saleableOptions = (item) => {
  const quantity = Number(item?.item_quantity) || 0;
  return Array.from({ length: quantity + 1 }, (_, index) => quantity - index);
};

// The product's pallet (from its product_pallets) for a chosen pallet.
export const palletOf = (item, palletId) =>
  item?.products?.product_pallets?.find(
    (pallet) =>
      String(pallet?.pallet_id ?? pallet?.pallet?.id) === String(palletId),
  );

// The line's lots, as the desktop table shows them: "LOT-A7 (4)".
const lotsOf = (item) =>
  (item?.order_item_product_lot_quantities ?? [])
    .map((lot) =>
      lot?.product_lot?.name ? `${lot.product_lot.name} (${lot.quantity})` : "",
    )
    .filter(Boolean)
    .join(", ");

// A name cut to two lines.
const CLAMP_2 =
  "overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]";

// The header and every row share these columns. Attention: product, qty,
// Saleable (switch and quantity), Note. Fulfilled: product, qty, pallet.
const ATTENTION_COLS = "grid-cols-[minmax(0,1fr)_26px_38px_48px_32px]";
const FULFILLED_COLS = "grid-cols-[minmax(0,1fr)_26px_104px]";

// A small quantity menu (the table's is 40px tall).
const COMPACT_SELECT = {
  width: "100%",
  fontSize: 13,
  "& .MuiSelect-select": {
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "8px",
    paddingRight: "18px !important",
    minHeight: "0 !important",
    lineHeight: "20px",
  },
  "& .MuiSelect-icon": { right: 0 },
};

// Typing in a 16px field doesn't zoom the page on iPhones.
const PHONE_FIELD = {
  "& textarea, & input": { fontSize: 16 },
  "& input": {
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "8px",
    paddingRight: "4px",
  },
};

/**
 * The Sale Status Update pop-up's products on phones: one row per product,
 * as on the desktop table, under one header — picture, name and SKU, ×qty,
 * then the row's controls on the same line: Saleable and its quantity and a
 * Note button (Attention), or the pallet it comes from (Fulfilled). A note
 * field or the pallet list opens under its row when asked for.
 */
const StatusLinesPhone = ({
  lines,
  attention,
  fulfilled,
  onSaleable,
  onSaleableQty,
  onNote,
  openNotes,
  onOpenNote,
  noteFields,
  expanded,
  onToggle,
  onPalletToggle,
  onPalletQty,
}) => {
  const { t } = useTranslation(["warehouse", "common"]);
  // Names cut to two lines; a tap shows the whole name.
  const [fullNames, setFullNames] = useState({});
  const cols = attention ? ATTENTION_COLS : FULFILLED_COLS;

  return (
    <div className="overflow-hidden rounded-[8px] border-[1px] border-ui-border">
      <div
        aria-hidden="true"
        className={`grid ${cols} items-end gap-x-[6px] border-b-[1px] border-ui-border bg-ui-surface-sunken px-[8px] py-[6px] text-[10px] font-semibold uppercase leading-[1.2] tracking-[0.05em] text-ui-text-muted`}
      >
        <span>
          {t("orderDetails.product")} · {lines.length}
        </span>
        <span className="text-center">{t("orderDetails.qty")}</span>
        {attention && (
          <>
            <span className="col-span-2 text-center">
              {t("orderDetails.saleable")}
            </span>
            <span className="text-center">{t("orderDetails.note")}</span>
          </>
        )}
        {fulfilled && !attention && (
          <span className="text-center">{t("orderDetails.pallet")}</span>
        )}
      </div>

      <div role="list" aria-label={t("orderDetails.productDetails")}>
        {lines.map((item, index) => {
          const name = item?.products?.name ?? item?.product_name ?? "-";
          const sku = item?.products?.code;
          const lots = lotsOf(item);
          const note = productNote(item);
          const noteShown =
            Boolean(openNotes[index]) || Boolean(item?.fulfillment_note);
          const fullName = Boolean(fullNames[index]);
          return (
            <div
              key={item?.id ?? index}
              role="listitem"
              className="border-b-[1px] border-ui-border px-[8px] py-[6px] last:border-b-0 even:bg-ui-surface-sunken"
            >
              <div className={`grid ${cols} items-center gap-x-[6px]`}>
                <div className="flex min-w-0 items-center gap-[6px]">
                  <img
                    src={item?.products?.media?.[0]?.file || noImage}
                    alt=""
                    className="h-[28px] w-[28px] shrink-0 rounded-[4px] object-cover"
                    onError={(e) => {
                      e.target.src = noImage;
                    }}
                  />
                  <button
                    type="button"
                    aria-expanded={fullName}
                    onClick={() =>
                      setFullNames((prev) => ({
                        ...prev,
                        [index]: !prev[index],
                      }))
                    }
                    className="min-w-0 flex-1 bg-transparent p-0 text-left"
                  >
                    <span
                      className={`break-words text-[12px] font-semibold leading-[1.3] text-ui-text ${
                        fullName ? "block" : CLAMP_2
                      }`}
                    >
                      {name}
                    </span>
                    {(sku || lots) && (
                      <span
                        className={`block font-mono text-[10.5px] leading-[1.45] text-ui-text-muted ${
                          fullName ? "break-all" : "truncate"
                        }`}
                      >
                        {[sku, lots].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </button>
                </div>
                <span className="text-center text-[13px] font-semibold tabular-nums text-ui-text">
                  ×{item?.item_quantity ?? "-"}
                </span>

                {attention && (
                  <>
                    <div className="flex justify-center">
                      <Switch
                        size="small"
                        checked={item?.is_saleable || false}
                        onChange={(e) => onSaleable(e, index)}
                        color="primary"
                        inputProps={{
                          "aria-label": `${t("orderDetails.saleable")}: ${name}`,
                        }}
                      />
                    </div>
                    <Select
                      size="small"
                      value={item?.saleable_item_quantity || "0"}
                      onChange={(e) => onSaleableQty(e, index)}
                      inputProps={{
                        "aria-label": `${t("orderDetails.saleableQty")}: ${name}`,
                      }}
                      sx={COMPACT_SELECT}
                    >
                      {saleableOptions(item).map((quantity) => (
                        <MenuItem key={quantity} value={quantity}>
                          {quantity}
                        </MenuItem>
                      ))}
                    </Select>
                    <button
                      type="button"
                      aria-label={`${t("orderDetails.note")}: ${name}`}
                      aria-expanded={noteShown}
                      onClick={() => onOpenNote(index)}
                      className={`mx-auto flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border-[1px] transition-colors ${
                        noteShown
                          ? "border-brand bg-brand-light text-brand"
                          : "border-ui-border bg-ui-surface text-ui-text-muted hover:border-brand hover:text-brand"
                      }`}
                    >
                      <MdEditNote size={17} aria-hidden="true" />
                    </button>
                  </>
                )}

                {fulfilled && !attention && (
                  <PalletButton
                    item={item}
                    open={Boolean(expanded[index])}
                    onClick={() => onToggle(index)}
                    t={t}
                  />
                )}
              </div>

              {/* The shipper's note on this product: shown to whoever
                  fulfils it, never edited here. */}
              {note && (
                <p className="m-0 mt-[4px] pl-[34px] text-[11.5px] leading-[1.4] text-state-warning-text">
                  <b>{t("orderDetails.productNoteColon")}</b> {note}
                </p>
              )}

              {attention && noteShown && (
                <div className="mt-[6px] pl-[34px]">
                  <TextField
                    inputRef={(field) => {
                      noteFields.current[index] = field;
                    }}
                    placeholder={t("orderDetails.fulfilmentNote2")}
                    value={item?.fulfillment_note ?? ""}
                    onChange={(e) => onNote(e, index)}
                    inputProps={{
                      "aria-label": `${t("orderDetails.fulfilmentNote")}: ${name}`,
                    }}
                    multiline
                    size="small"
                    fullWidth
                    sx={PHONE_FIELD}
                  />
                </div>
              )}

              {fulfilled && !attention && expanded[index] && (
                <PalletList
                  item={item}
                  index={index}
                  onPalletToggle={onPalletToggle}
                  onPalletQty={onPalletQty}
                  t={t}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Where a Fulfilled line comes from: the chosen pallet (or how many), its
// location and quantity. Opens the pallet list under the row.
const PalletButton = ({ item, open, onClick, t }) => {
  const chosen = item?.pallet_data ?? [];
  const first = chosen[0] && palletOf(item, chosen[0].pallet_id);
  let main;
  let detail;
  if (chosen.length === 0) {
    main = (
      <span className="block truncate text-ui-text-subtle">
        {t("orderDetails.noPallet")}
      </span>
    );
    detail = (
      <span className="block truncate font-medium text-brand">
        {t("orderDetails.choosePallet")}
      </span>
    );
  } else if (chosen.length === 1) {
    main = (
      <span className="block truncate font-semibold text-ui-text">
        {first?.pallet?.name ?? first?.pallet?.code ?? "N/A"}
      </span>
    );
    detail = (
      <span className="block truncate text-ui-text-muted">
        {[first?.pallet?.location?.name, `×${chosen[0]?.quantity ?? 0}`]
          .filter(Boolean)
          .join(" · ")}
      </span>
    );
  } else {
    main = (
      <span className="block truncate font-semibold text-ui-text">
        {t("orderDetails.palletCount", { count: chosen.length })}
      </span>
    );
    detail = (
      <span className="block truncate text-ui-text-muted">
        {chosen
          .map(
            (pallet) =>
              palletOf(item, pallet.pallet_id)?.pallet?.location?.name,
          )
          .filter(Boolean)
          .join(", ")}
      </span>
    );
  }
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onClick}
      className={`flex min-h-[40px] min-w-0 items-center gap-[2px] rounded-[6px] border-[1px] bg-ui-surface py-[3px] pl-[7px] pr-[2px] text-left text-[11.5px] leading-[1.35] transition-colors ${
        open ? "border-brand" : "border-ui-border hover:border-brand"
      }`}
    >
      <span className="min-w-0 flex-1">
        {main}
        {detail}
      </span>
      <MdKeyboardArrowDown
        size={18}
        aria-hidden="true"
        className={`shrink-0 text-ui-text-muted transition-transform ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
  );
};

// The product's pallets, one per line: tick the ones it comes from, with how
// many from each.
const PalletList = ({ item, index, onPalletToggle, onPalletQty, t }) => {
  const pallets = item?.products?.product_pallets ?? [];
  const chosen = item?.pallet_data ?? [];
  // A chosen pallet the product no longer lists still shows, to untick.
  const unlisted = chosen.filter((pallet) => !palletOf(item, pallet.pallet_id));
  const setQuantity = (at, value) =>
    onPalletQty(
      chosen.map((pallet, i) =>
        i === at ? { ...pallet, quantity: value } : pallet,
      ),
      index,
    );

  const row = (key, palletId, label, code, details, stock) => {
    const at = chosen.findIndex(
      (pallet) => String(pallet?.pallet_id) === String(palletId),
    );
    const quantity = at >= 0 ? chosen[at].quantity : null;
    const tooMany =
      at >= 0 && stock != null && parseInt(quantity, 10) > parseInt(stock, 10);
    return (
      <div
        key={key}
        className="grid grid-cols-[auto_minmax(0,1fr)_64px] items-center gap-x-[4px] border-b-[1px] border-ui-border py-[2px] pr-[6px] last:border-b-0"
      >
        <Checkbox
          size="small"
          checked={at >= 0}
          onChange={() => onPalletToggle(palletId, index)}
          inputProps={{ "aria-label": label }}
        />
        <div className="min-w-0">
          <div className="truncate text-[12.5px] font-semibold leading-[1.35] text-ui-text">
            {label}
            {code && (
              <span className="ml-[6px] font-mono text-[10.5px] font-normal text-ui-text-muted">
                {code}
              </span>
            )}
          </div>
          <div className="truncate text-[11px] leading-[1.4] text-ui-text-muted">
            {details}
          </div>
        </div>
        {at >= 0 ? (
          <TextField
            type="number"
            size="small"
            value={quantity ?? ""}
            onChange={(e) => setQuantity(at, e.target.value)}
            error={tooMany}
            inputProps={{
              "aria-label": `${t("orderDetails.qty")}: ${label}`,
              inputMode: "numeric",
              min: 0,
            }}
            sx={PHONE_FIELD}
          />
        ) : (
          <span />
        )}
      </div>
    );
  };

  return (
    <div className="mt-[6px] overflow-hidden rounded-[6px] border-[1px] border-ui-border bg-ui-surface">
      {pallets.length === 0 && unlisted.length === 0 && (
        <p className="m-0 px-[10px] py-[8px] text-[12px] text-ui-text-subtle">
          {t("orderDetails.noPalletsForProduct")}
        </p>
      )}
      {pallets.map((pallet, idx) =>
        row(
          `p${idx}`,
          pallet?.pallet_id ?? pallet?.pallet?.id,
          pallet?.pallet?.name ?? pallet?.pallet?.code ?? "N/A",
          pallet?.pallet?.name ? pallet?.pallet?.code : null,
          [
            pallet?.pallet?.location?.name,
            pallet?.product_lot?.name,
            t("orderDetails.inStock", { count: pallet?.quantity ?? 0 }),
          ]
            .filter(Boolean)
            .join(" · "),
          pallet?.quantity,
        ),
      )}
      {unlisted.map((pallet, idx) =>
        row(`u${idx}`, pallet.pallet_id, "N/A", null, "", null),
      )}
    </div>
  );
};

export default StatusLinesPhone;
